class LinkedObjects {
	url;
	objectId;

	constructor( url, objectId ) {
		this.url = url;
		this.objectId = objectId;
	}

	request( sort = 'dateDesc', paged = 0 ) {
		const params = new URLSearchParams( {
			paged,
			sort,
		} );

		return fetch( this.url + '?' + params.toString() )
			.then( response => {
				if ( ! response.ok ) {
					throw new Error( `Discover Request Status: ${ response.status }` );
				}

				return response.json();
			} )
			.catch( () => {
				// do nothing
			} );
	}
}

function getVar( data, variable ) {
	switch ( variable ) {
		case 'START_DATE':
		case 'END_DATE':
			const date = variable === 'END_DATE' ? data.date2 : data.date1;
			if ( ! date ) {
				return false;
			}
			const dateObj = new Date( date.dateTime );
			return dateObj.toLocaleDateString( 'en-ca', {
				day: 'numeric',
				month: 'short',
				year: 'numeric',
			} );

		case 'IS_FAVOURITED':
			if ( ! data.favouritedByUserIds ) {
				return false;
			}
			return data.favouritedByUserIds.length > 0;

		case 'AUTHOR_COUNT':
			if ( ! data.authors ) {
				return false;
			}
			return data.authors.length;

		case 'AUTHOR_NAMES':
			if ( ! data.authors || data.authors.length === 0 ) {
				return false;
			}

			const authors = [ ...data.authors ];

			const elements = authors.map( ( author, index ) => {
				const elm = document.createElement( 'span' );
				elm.className = 'ihpme-snippet__author';
				elm.innerText = `${ index ? ', ' : '' }${ author.nameShortFormat }`;
				return elm;
			} );

			if ( authors.length > 3 ) {
				const button = document.createElement( 'button' );
				button.className = 'ihpme-snippet__author-list__toggle';
				button.innerText = `${ authors.length - 3 } more`;
				elements.push( button );
			}

			return elements;

		case 'SOURCE':
			const { volume, issue, pagination } = data;
			if ( ! volume && ! issue && ! pagination ) {
				return false;
			}

			const text = [];
			if ( volume ) {
				text.push( volume );
			}
			if ( issue ) {
				// wrap issue in parentheses
				text.push( `(${ issue })` );
			}
			if ( pagination ) {
				// append pagination, separate with colon
				if ( text.length > 0 ) {
					text.push( ':' );
				}
				text.push( pagination );
			}

			return text.join( '' );

		default:
			return data[ variable ] ?? false;
	}
}

function fillVars( template, data ) {
	return template.replaceAll( /%([^%]*)\[(\w+)\]([^%]*)%/g, ( match, before, variable, after ) => {
		const value = getVar( data, variable );
		if ( ! value ) {
			return '';
		}

		return `${ before }${ value }${ after }`;
	} );
}

function populateElement( node, data ) {
	node.childNodes.forEach( _node => {
		const populated = populateElement( _node, data );
		if ( ! populated ) {
			node.removeChild( _node );
		}
	} );

	let show = true;
	if ( node.dataset?.vars ) {
		const vars = node.dataset.vars.split( ',' );

		show = false;
		for ( const variable of vars ) {
			if ( getVar( data, variable ) !== false ) {
				show = true;
			}
		}
	}

	if ( ! show ) {
		return false;
	}

	if ( node.dataset?.attribute ) {
		const [ attr, template ] = node.dataset.attribute.split( '|' );
		node.setAttribute( attr, fillVars( template, data ) );
	}

	if ( node.dataset?.content ) {
		const variable = node.dataset.content;
		const output = getVar( data, variable );
		if ( output instanceof Array ) {
			output.forEach( elm => {
				node.appendChild( elm );
			} );
		} else {
			node.appendChild( output );
		}
	}

	if ( node.nodeName === '#text' ) {
		node.nodeValue = fillVars( node.nodeValue, data );
	}

	return true;
}

export function setupDiscoverModule( container ) {
	const url = container.dataset.discoverUrl;
	const id = container.dataset.discoverId;

	const objects = new LinkedObjects( url, id );

	const template = container.querySelector( '.js-discover-snippet-template' )?.content.firstElementChild;
	const wrapper = container.querySelector( '.js-discover-results' );

	const paging = container.querySelector( '.js-discover-pagination' );
	const pageFirst = container.querySelector( '.js-discover-pagination-first' );
	const pagePrev = container.querySelector( '.js-discover-pagination-prev' );
	const pageNext = container.querySelector( '.js-discover-pagination-next' );
	const pageLast = container.querySelector( '.js-discover-pagination-last' );
	const pageCount = container.querySelector( '.js-discover-page-count' );
	const itemCount = container.querySelector( '.js-discover-item-count' );
	const sortControl = container.querySelector( '.js-discover-sort' );

	let page = 0;
	let pages = 1;
	function fetchResults( sortBy, paged ) {
		container.classList.add( 'loading' );

		objects.request( sortBy, paged )
			.then( ( { resource = [], pagination: { perPage, startFrom, total } = {}, sort } ) => {
				wrapper.innerHTML = '';
				resource.forEach( entry => {
					const snippet = template.cloneNode( true );
					populateElement( snippet, entry );

					wrapper.appendChild( snippet );
				} );

				page = Math.ceil( startFrom / perPage ) + 1;
				pages = Math.ceil( total / perPage );

				paging.hidden = total <= perPage;
				if ( ! paging.hidden ) {
					pageFirst.disabled = page <= 2;
					pagePrev.disabled = page <= 1;
					pageNext.disabled = page >= pages;
					pageLast.disabled = page >= pages - 1;
					pageCount.innerText = `${ page } of ${ pages }`;
					itemCount.innerText = `${ startFrom + 1 }–${ Math.min( total, startFrom + perPage ) } out of ${ total }`;
				}

				sortControl.value = sort;

				container.classList.remove( 'loading' );
			} );
	}

	sortControl.addEventListener( 'change', () => fetchResults( sortControl.value, page ) );
	pageFirst.addEventListener( 'click', () => fetchResults( sortControl.value, 1 ) );
	pagePrev.addEventListener( 'click', () => fetchResults( sortControl.value, page - 1 ) );
	pageNext.addEventListener( 'click', () => fetchResults( sortControl.value, page + 1 ) );
	pageLast.addEventListener( 'click', () => fetchResults( sortControl.value, pages ) );

	fetchResults( sortControl.value, 1 );
}

export default function setupDiscoverModules() {
	document.querySelectorAll( '.js-discover-module' ).forEach( setupDiscoverModule );
}
